import React from 'react'
import { TimesheetDayActivityModel } from 'app/models/timesheet/timesheetDayActivityModel'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { FaIcon, faUserSolid, faTruckSolid } from '@/lib/fa'
import { getActivityColor } from '@/modules/drivers/helpers/ActivityColorResolver'

const dayOfWeekKeys = [
  'dayOfWeek.sunday-short',
  'dayOfWeek.monday-short',
  'dayOfWeek.tuesday-short',
  'dayOfWeek.wednesday-short',
  'dayOfWeek.thursday-short',
  'dayOfWeek.friday-short',
  'dayOfWeek.saturday-short',
]

const VEHICLE_SOURCE_NUMBERS = new Set<number>([3, 4, 9])

const MIN_SIGNIFICANT_BLOCK_HOURS = 2

const HeaderDayItem = React.memo(
  ({
    day,
    height,
    width,
    isSelected = false,
    isFirstSelected = false,
    isLastSelected = false,
    isHovered = false,
    onClick,
    onMouseDown,
    onMouseEnter,
    onContextMenu,
  }: {
    day: TimesheetDayActivityModel
    height: number
    width: number
    isSelected?: boolean
    isFirstSelected?: boolean
    isLastSelected?: boolean
    isHovered?: boolean
    onClick?: (e: React.MouseEvent) => void
    onMouseDown?: (e: React.MouseEvent) => void
    onMouseEnter?: () => void
    onContextMenu?: (e: React.MouseEvent) => void
  }) => {
    const { t } = useLocalize()
    const dayOfWeekIndex = new Date(
      day?.activityDayLocalTimeDate as any,
    ).getDay()
    const shortDayName = t(dayOfWeekKeys[dayOfWeekIndex])

    const sourceChangeIcons = React.useMemo(() => {
      const blocks = day?.activityBlocks
      if (!blocks || blocks.length === 0) return []
      const changes: { offset: number; isVehicle: boolean }[] = []
      let cumulative = 0
      let lastSignificantSource: number | null = null
      for (const block of blocks) {
        if (block.count >= MIN_SIGNIFICANT_BLOCK_HOURS) {
          if (block.source !== lastSignificantSource) {
            changes.push({
              offset: cumulative,
              isVehicle: VEHICLE_SOURCE_NUMBERS.has(block.source),
            })
            lastSignificantSource = block.source
          }
        }
        cumulative += block.count
      }
      return changes
    }, [day?.activityBlocks])

    return (
      <div
        className={`tw-relative tw-box-border tw-flex tw-cursor-pointer tw-select-none tw-flex-col tw-items-start tw-justify-center tw-gap-1 tw-border-[1px] tw-border-solid tw-pl-[2px] ${
          isSelected
            ? `tw-border-y-[#2C57FF] tw-border-b-transparent ${isFirstSelected ? 'tw-rounded-tl-md tw-border-l-[#2C57FF]' : 'tw-border-l-transparent'} ${isLastSelected ? 'tw-rounded-tr-md tw-border-r-primary' : 'tw-border-r-transparent'}`
            : 'tw-border-transparent'
        }`}
        style={{ height, minWidth: width, flex: 1 }}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
        onContextMenu={onContextMenu}
      >
        {isSelected && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-[#0034FF]/15" />
        )}
        {!isSelected && isHovered && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-[#0034FF]/10" />
        )}
        <div
          className="tw-flex tw-absolute tw-gap-[2px] tw-flex-col"
          style={{
            right: 2,
            bottom: 10,
          }}
        >
          {sourceChangeIcons.map((change, i) => (
              <FaIcon
                key={i}
                icon={change.isVehicle ? faTruckSolid : faUserSolid}
                className={`tw-h-2 tw-w-2 ${
                  isSelected ? 'tw-text-primary' : 'tw-text-content-base'
                }`}
              />
          ))}
        </div>
        <span
          className={`tw-text-[10px] tw-font-semibold tw-leading-none ${day?.isHoliday && !isSelected ? 'tw-text-red-500' : isSelected ? 'tw-text-primary' : ''}`}
        >
          {day?.dayNumber}
        </span>
        <span
          className={`tw-text-[10px] tw-leading-none tw-text-content-base ${day?.isHoliday && !isSelected ? 'tw-text-red-500' : isSelected ? 'tw-text-primary' : ''}`}
        >
          {shortDayName}
        </span>
        <div className="tw-absolute tw-bottom-0 tw-left-0 tw-z-10 tw-flex tw-h-[3px] tw-w-full">
          {day?.tripData?.map((seg, i) => (
            <div
              key={i}
              style={{
                width: `${(seg.count / 24) * 100}%`,
                background: seg.trip ? '#B200FC' : '#transparent',
              }}
            />
          ))}
        </div>
        {day?.activityBlocks && day.activityBlocks.length > 0 && (
          <div className="tw-absolute tw-bottom-[3px] tw-left-0 tw-z-10 tw-flex tw-h-[3px] tw-w-full">
            {day.activityBlocks.map((block, i) => {
              const color = getActivityColor({
                type: block.type,
                isVehicleSource: VEHICLE_SOURCE_NUMBERS.has(block.source),
              } as any)
              return (
                <div
                  key={i}
                  style={{
                    width: `${(block.count / 24) * 100}%`,
                    background: color,
                  }}
                />
              )
            })}
          </div>
        )}
      </div>
    )
  },
)

HeaderDayItem.displayName = 'HeaderDayItem'

export default HeaderDayItem
